import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "antd";

import {
  INITIAL_FILTERS,
  MAX_PRICE_PER_KG,
  MAX_VOLUME_KG,
  MIN_PRICE_PER_KG,
  MIN_RADIUS_KM,
  MIN_VOLUME_KG,
  PRICE_STEP_PER_KG,
  RADIUS_STEP_KM,
  VOLUME_STEP_KG,
  countActiveFilters,
  normaliseFilters,
  radiusSliderMaxKm,
} from "../../search/supplierFilters";

const RadioRow = ({ legend, options, value, onChange }) => (
  <fieldset className="home-filter-group">
    <legend>{legend}</legend>

    <div className="home-filter-chips" role="radiogroup" aria-label={legend}>
      {options.map((option) => {
        const selected = value === option.id;

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`home-filter-chip${selected ? " is-selected" : ""}`}
            onClick={() => onChange(selected ? null : option.id)}
          >
            {option.label || option.defaultTitle}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const SliderRow = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  valueText,
  onChange,
}) => {
  const [preview, setPreview] = useState(value);
  const inputId = `filter-${label.replace(/\s+/g, "-").toLowerCase()}`;

  useEffect(() => {
    setPreview(value);
  }, [value]);

  const percent = max === min ? 0 : (preview - min) / (max - min);

  const onInput = (event) => {
    const next = Number(event.target.value);

    setPreview(next);
    onChange(next);
  };

  return (
    <div className="home-filter-group">
      <div className="home-filter-head">
        <label htmlFor={inputId}>{label}</label>
        <output htmlFor={inputId}>
          {preview}
          {unit}
        </output>
      </div>

      <div className="home-slider" style={{ "--pct": percent }}>
        <span className="home-slider-bubble" aria-hidden="true">
          {preview}
          <small>{unit.trim()}</small>
        </span>

        <div className="home-slider-rail">
          <div className="home-slider-track" aria-hidden="true">
            <span className="home-slider-fill" />
          </div>

          <input
            id={inputId}
            className="home-slider-input"
            type="range"
            min={min}
            max={max}
            step={step}
            value={preview}
            aria-valuetext={valueText(preview)}
            onChange={onInput}
          />
        </div>

        <div className="home-slider-scale" aria-hidden="true">
          <span>
            {min}
            {unit}
          </span>
          <span>
            {max}
            {unit}
          </span>
        </div>
      </div>
    </div>
  );
};

const FiltersPanel = ({
  open,
  filters,
  containerOptions,
  containerTypes,
  asSheet,
  onClose,
  onApply,
  onReset,
}) => {
  const [draft, setDraft] = useState(() => normaliseFilters(filters));
  const pendingRef = useRef(null);

  useEffect(() => {
    if (open) setDraft(normaliseFilters(filters));
  }, [open, filters]);

  const activeCount = useMemo(() => countActiveFilters(draft), [draft]);

  const radiusMax = useMemo(
    () => radiusSliderMaxKm(Math.max(draft.radiusKm, filters.radiusKm)),
    [draft.radiusKm, filters.radiusKm]
  );

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const tapApply = () => {
    pendingRef.current = { type: "apply", values: draft };
    onClose();
  };

  const tapReset = () => {
    pendingRef.current = { type: "reset" };
    onClose();
  };

  const onAfterClose = (isOpen) => {
    if (isOpen) return;

    const pending = pendingRef.current;
    pendingRef.current = null;

    if (!pending) return;
    if (pending.type === "reset") onReset();
    else onApply(pending.values);
  };

  useEffect(() => {
    if (!open) return;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={onAfterClose}
      placement={asSheet ? "bottom" : "right"}
      height={asSheet ? "88%" : undefined}
      width={asSheet ? undefined : "min(440px, 100vw)"}
      title={
        <header className="home-panel-header-card">
          <div className="home-panel-heading">
            <h2 className="home-panel-title">Filters</h2>
            <p className="home-panel-subtitle">
              {activeCount > 0
                ? `${activeCount} ${activeCount === 1 ? "filter" : "filters"} applied`
                : "Narrow down what you see"}
            </p>
          </div>

          <button
            type="button"
            className="home-panel-close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <img
              src="/assets/icons/panel-close.svg"
              width="24"
              height="24"
              alt=""
              aria-hidden="true"
            />
          </button>
        </header>
      }
      closable={false}
      rootClassName="home-filters-drawer home-screen-filters-drawer"
      destroyOnClose={false}
      footer={
        <div className="home-filter-footer">
          <button
            type="button"
            className="home-filter-reset"
            disabled={countActiveFilters(filters) === 0}
            onClick={tapReset}
          >
            Reset
          </button>

          <button type="button" className="home-filter-apply" onClick={tapApply}>
            Apply{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </div>
      }
    >
      <RadioRow
        legend="Purchase Type"
        options={containerOptions}
        value={draft.containerOptionId}
        onChange={(containerOptionId) => update({ containerOptionId })}
      />

      <RadioRow
        legend="Cylinder Type"
        options={containerTypes}
        value={draft.containerTypeId}
        onChange={(containerTypeId) => update({ containerTypeId })}
      />

      <SliderRow
        label="Cylinder Volume"
        value={draft.maxVolume}
        min={MIN_VOLUME_KG}
        max={MAX_VOLUME_KG}
        step={VOLUME_STEP_KG}
        unit="kg"
        valueText={(value) => `up to ${value} kilograms`}
        onChange={(maxVolume) => update({ maxVolume })}
      />

      <details className="home-filter-advanced" open>
        <summary>Advanced Filters</summary>

        <SliderRow
          label="Max Price per kg"
          value={draft.maxPricePerKg}
          min={MIN_PRICE_PER_KG}
          max={MAX_PRICE_PER_KG}
          step={PRICE_STEP_PER_KG}
          unit=" GHS"
          valueText={(value) => `up to ${value} cedis per kilogram`}
          onChange={(maxPricePerKg) => update({ maxPricePerKg })}
        />

        <SliderRow
          label="Radius"
          value={draft.radiusKm}
          min={MIN_RADIUS_KM}
          max={radiusMax}
          step={RADIUS_STEP_KM}
          unit="km"
          valueText={(value) => `${value} kilometres`}
          onChange={(radiusKm) => update({ radiusKm })}
        />

        {draft.maxPricePerKg === INITIAL_FILTERS.maxPricePerKg && (
          <p className="home-filter-hint">
            Price is unfiltered at the maximum.
          </p>
        )}
      </details>
    </Drawer>
  );
};

export default React.memo(FiltersPanel);
